import Controller from '@ember/controller';
import { type PropertiesPropertyRouteModel } from 'donor-elf-web/routes/funds/fund/properties/property';
import ContactsCache, {
  isGroupsContactCustomPropertyInfo,
  PropertyTypeLabels,
} from 'donor-elf-web/services/contacts-cache';
import { service } from '@ember/service';
import { action } from '@ember/object';
import RouterService from '@ember/routing/router-service';
import { tracked } from '@glimmer/tracking';

export interface PropertyOptionAndCount {
  option: string;
  count: number;
}

export default class PropertiesPropertyIndexController extends Controller {
  @service declare contactsCache: ContactsCache;
  @service declare router: RouterService;

  @tracked model!: PropertiesPropertyRouteModel;

  @tracked deleteOptionAndCount?: PropertyOptionAndCount;

  get propertyLabel() {
    return this.model.property.l;
  }

  get isGroupsProperty() {
    return isGroupsContactCustomPropertyInfo(this.model.property);
  }

  get contactCountWithProperty() {
    if (this.isGroupsProperty) {
      return this.contactsCache.contacts.filter((x) => x.hasGroups).length;
    } else {
      let { propertyLabel } = this;
      return this.contactsCache.contacts.filter(
        (x) => x.customPropertiesByLabel[propertyLabel] != null,
      ).length;
    }
  }

  get contactsFilterPropertyName() {
    if (this.isGroupsProperty) {
      return 'contactGroups';
    } else {
      return `contact_custom_${this.propertyLabel}`;
    }
  }

  get propertyTypeLabel() {
    return PropertyTypeLabels[this.model.property.t];
  }

  get propertyIsReadonly() {
    return this.model.property.r;
  }

  get propertyTypeIsList() {
    return this.model.property.t === 'l';
  }

  get propertySettingsSingleSelection() {
    return this.model.property.s?.single_selection;
  }

  get propertySettingsCantEditOptions() {
    return this.model.property.s?.cant_edit_options;
  }

  get propertySettingsCantDelete() {
    return this.model.property.s?.cant_delete;
  }

  get propertyOptionsAndCount(): PropertyOptionAndCount[] {
    let { propertyLabel } = this;
    if (this.isGroupsProperty) {
      return this.model.property!.o!.map((option) => {
        return {
          option,
          count: this.contactsCache.contacts.filter((x) =>
            x.groups?.includes(option),
          ).length,
        };
      });
    } else {
      return this.model.property!.o!.map((option) => {
        return {
          option,
          count: this.contactsCache.contacts.filter(
            (x) => x.customPropertiesByLabel[propertyLabel]?.v?.[option],
          ).length,
        };
      });
    }
  }

  get actionMenuItems() {
    return [
      {
        label: 'Rename',
        svgJar: 'edit',
        route: 'funds.fund.properties.property.rename',
      },
      {
        label: 'Delete',
        svgJar: 'trash',
        route: 'funds.fund.properties.property.delete',
      },
    ];
  }

  @action
  handleDeleteOption(optionAndCount: PropertyOptionAndCount, dropdown: any) {
    this.deleteOptionAndCount = optionAndCount;
    dropdown.actions.close();
  }

  @action
  handleDeleteOptionDeleted(_option: string) {
    this.deleteOptionAndCount = undefined;
    this.send('refreshPropertyRouteModel');
  }

  @action
  handleDeleteOptionClose() {
    this.deleteOptionAndCount = undefined;
  }
}
